import React, { useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as Fi from 'react-icons/fi';

const TAG_INFO = {
  winner:        { emoji: '🏸', label: '致勝球',  color: '#34d399' },
  error:         { emoji: '😬', label: '主動失誤', color: '#fb7185' },
  serve_fault:   { emoji: '⚡', label: '發球失誤', color: '#f59e0b' },
  receive_fault: { emoji: '🫳', label: '接發失誤', color: '#a78bfa' },
  out:           { emoji: '↗️', label: '出界',    color: '#60a5fa' },
  net:           { emoji: '🥅', label: '下網',    color: '#f97316' },
};

// Extract tag key from log entry e.g. "[🏸]" → find matching TAG_INFO
const findTagInfo = (entry) => {
  const match = entry.match(/\[([^\]]+)\]$/);
  if (!match) return null;
  const emoji = match[1];
  return Object.values(TAG_INFO).find(t => t.emoji === emoji) || null;
};

const ScoreLogDrawer = ({ open, onClose, log, canUndo, canRedo, onUndo, onRedo, onShowStats, hasStats }) => {
  const listRef = useRef(null);

  useEffect(() => {
    if (open && listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [open, log]);

  const renderEntry = (entry, i) => {
    const isSep = entry.startsWith('───') || entry.startsWith('↔') || entry.startsWith('🏆') || entry.startsWith('🎯');
    const isUndo = entry.startsWith('↩');
    const isRedo = entry.startsWith('↪');
    const isLet = entry.startsWith('🔄');
    const isDispute = entry.startsWith('⚖️');

    const tagInfo = (!isSep && !isUndo && !isRedo) ? findTagInfo(entry) : null;
    const cleanEntry = tagInfo ? entry.replace(/\s*\[[^\]]+\]$/, '') : entry;

    if (isSep) {
      return (
        <div key={i} className="flex items-center gap-2 py-2 px-3">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-[10px] font-black text-white/30 whitespace-nowrap">{entry}</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      );
    }

    if (isUndo || isRedo) {
      return (
        <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold ${isUndo ? 'text-amber-400/80 bg-amber-500/8' : 'text-indigo-400/80 bg-indigo-500/8'}`}>
          <span>{entry}</span>
        </div>
      );
    }

    if (isLet || isDispute) {
      return (
        <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-purple-300/80 bg-purple-500/8">
          <span>{entry}</span>
        </div>
      );
    }

    // Normal score entry
    return (
      <div key={i} className="flex items-start gap-2.5 px-3 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.06]">
        <span className="text-white/25 font-black text-xs w-5 text-right shrink-0 mt-0.5">{i + 1}</span>
        <span className="flex-1 text-sm font-bold text-white/80 leading-snug">{cleanEntry}</span>
        {tagInfo && (
          <div className="flex items-center gap-1 shrink-0 px-2 py-0.5 rounded-full border"
            style={{ backgroundColor: `${tagInfo.color}18`, borderColor: `${tagInfo.color}40` }}>
            <span className="text-sm">{tagInfo.emoji}</span>
            <span className="text-[10px] font-black" style={{ color: tagInfo.color }}>{tagInfo.label}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 bg-slate-950/80 backdrop-blur-sm" onClick={onClose} />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative z-10 w-full max-w-md bg-[#0a1220] border border-white/10 rounded-[2rem] overflow-hidden shadow-2xl flex flex-col max-h-[80vh]"
          >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between border-b border-white/[0.07] shrink-0">
              <div>
                <h3 className="text-base font-black text-white">得分記錄</h3>
                <p className="text-xs text-white/35 font-bold mt-0.5">{log.length} 條記錄</p>
              </div>
              <div className="flex items-center gap-2">
                {hasStats && (
                  <button onClick={() => { onClose(); setTimeout(() => onShowStats?.(), 150); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-500/20 border border-indigo-500/30 text-indigo-300 text-xs font-black hover:bg-indigo-500/30 transition-all">
                    <Fi.FiBarChart2 size={12} /> 統計
                  </button>
                )}
                <button onClick={onClose}
                  className="text-white/40 hover:text-white bg-white/5 px-3 py-1.5 rounded-xl text-sm font-bold transition-colors">
                  關閉
                </button>
              </div>
            </div>

            {/* Tag legend */}
            <div className="px-4 py-2.5 border-b border-white/[0.05] bg-white/[0.02] shrink-0">
              <p className="text-[9px] font-black text-white/25 uppercase tracking-widest mb-1.5">標記圖例</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.values(TAG_INFO).map(t => (
                  <div key={t.label} className="flex items-center gap-1 px-2 py-0.5 rounded-full border"
                    style={{ backgroundColor: `${t.color}12`, borderColor: `${t.color}30` }}>
                    <span className="text-xs">{t.emoji}</span>
                    <span className="text-[9px] font-black" style={{ color: t.color }}>{t.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Log list */}
            <div ref={listRef} className="px-4 py-3 space-y-1.5 overflow-y-auto flex-1"
              style={{ scrollbarWidth: 'none' }}>
              {log.length === 0 && (
                <p className="text-white/20 text-sm font-bold text-center py-8">尚無記錄</p>
              )}
              {log.map((entry, i) => renderEntry(entry, i))}
            </div>

            {/* Footer actions */}
            <div className="p-3 bg-white/[0.02] border-t border-white/[0.06] flex gap-3 shrink-0">
              <button onClick={onUndo} disabled={!canUndo}
                className="flex-1 py-3.5 bg-white/10 hover:bg-white/15 disabled:opacity-30 rounded-xl font-black text-sm text-white transition-colors flex items-center justify-center gap-2">
                <Fi.FiCornerUpLeft className="text-lg" /> 撤銷
              </button>
              <button onClick={onRedo} disabled={!canRedo}
                className="flex-1 py-3.5 bg-indigo-500/20 text-indigo-300 hover:bg-indigo-500/30 disabled:opacity-30 rounded-xl font-black text-sm transition-colors flex items-center justify-center gap-2">
                <Fi.FiCornerUpRight className="text-lg" /> 恢復
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default ScoreLogDrawer;